import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { 
  signInAnonymously, 
  onAuthStateChanged, 
  User as FirebaseUser 
} from 'firebase/auth';
import { auth, db } from '../config';

export interface UserPreferences {
  preferredResolution: string;
  adConsent: boolean;
  theme: 'light' | 'dark';
}

export interface AppUser {
  id: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  preferences: UserPreferences;
  createdAt: Date;
  lastActive: Date;
}

export interface UserFavorite {
  id: string;
  wallpaperId: string;
  addedAt: Date;
}

export interface UserDownload {
  id: string;
  wallpaperId: string;
  resolution: string;
  downloadedAt: Date;
  deviceType?: string; // Device type for device-specific downloads
  isFromPack?: boolean; // Whether download was from a wallpaper pack
}

class UserService {
  private usersCollection = 'users';
  private favoritesSubcollection = 'favorites';
  private downloadsSubcollection = 'downloads';

  async signInAnonymously(): Promise<FirebaseUser | null> {
    try {
      const result = await signInAnonymously(auth);
      return result.user;
    } catch (error) {
      console.error('Error signing in anonymously:', error);
      return null;
    }
  }

  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  }

  async createOrUpdateUser(firebaseUser: FirebaseUser): Promise<AppUser> {
    const userRef = doc(db, this.usersCollection, firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    const defaultPreferences: UserPreferences = {
      preferredResolution: 'auto',
      adConsent: false,
      theme: 'light',
    };

    if (userSnap.exists()) {
      // Update last active timestamp
      await updateDoc(userRef, {
        lastActive: serverTimestamp(),
      });
      
      const userData = userSnap.data();
      return {
        id: firebaseUser.uid,
        displayName: firebaseUser.displayName || undefined,
        email: firebaseUser.email || undefined,
        photoURL: firebaseUser.photoURL || undefined,
        preferences: userData.preferences || defaultPreferences,
        createdAt: userData.createdAt?.toDate() || new Date(),
        lastActive: new Date(),
      };
    } else {
      // Create new user document
      const newUser: Omit<AppUser, 'id'> = {
        displayName: firebaseUser.displayName || undefined,
        email: firebaseUser.email || undefined,
        photoURL: firebaseUser.photoURL || undefined,
        preferences: defaultPreferences,
        createdAt: new Date(),
        lastActive: new Date(),
      };

      await setDoc(userRef, {
        ...newUser,
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
      });

      return {
        id: firebaseUser.uid,
        ...newUser,
      };
    }
  }

  async updateUserPreferences(userId: string, preferences: Partial<UserPreferences>): Promise<void> {
    const userRef = doc(db, this.usersCollection, userId);
    await updateDoc(userRef, {
      preferences,
      lastActive: serverTimestamp(),
    });
  }

  async addToFavorites(userId: string, wallpaperId: string): Promise<void> {
    const favoritesRef = collection(db, this.usersCollection, userId, this.favoritesSubcollection);
    await addDoc(favoritesRef, {
      wallpaperId,
      addedAt: serverTimestamp(),
    });
  }

  async removeFromFavorites(userId: string, wallpaperId: string): Promise<void> {
    const favoritesRef = collection(db, this.usersCollection, userId, this.favoritesSubcollection);
    const q = query(favoritesRef);
    const querySnapshot = await getDocs(q);
    
    querySnapshot.forEach(async (docSnapshot) => {
      const data = docSnapshot.data();
      if (data.wallpaperId === wallpaperId) {
        await deleteDoc(docSnapshot.ref);
      }
    });
  }

  async getUserFavorites(userId: string): Promise<UserFavorite[]> {
    const favoritesRef = collection(db, this.usersCollection, userId, this.favoritesSubcollection);
    const q = query(favoritesRef, orderBy('addedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const favorites: UserFavorite[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      favorites.push({
        id: doc.id,
        wallpaperId: data.wallpaperId,
        addedAt: data.addedAt?.toDate() || new Date(),
      });
    });
    
    return favorites;
  }

  async isWallpaperFavorited(userId: string, wallpaperId: string): Promise<boolean> {
    const favoritesRef = collection(db, this.usersCollection, userId, this.favoritesSubcollection);
    const querySnapshot = await getDocs(favoritesRef);
    
    let isFavorited = false;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.wallpaperId === wallpaperId) {
        isFavorited = true;
      }
    });
    
    return isFavorited;
  }

  async recordDownload(
    userId: string, 
    wallpaperId: string, 
    resolution: string, 
    additionalData?: {
      deviceType?: string;
      isWallpaperPack?: boolean;
      timestamp?: string;
      [key: string]: any;
    }
  ): Promise<void> {
    const downloadsRef = collection(db, this.usersCollection, userId, this.downloadsSubcollection);
    await addDoc(downloadsRef, {
      wallpaperId,
      resolution,
      downloadedAt: serverTimestamp(),
      deviceType: additionalData?.deviceType || 'unknown',
      isFromPack: additionalData?.isWallpaperPack || false,
      ...additionalData
    });
  }

  async getUserDownloads(userId: string): Promise<UserDownload[]> {
    const downloadsRef = collection(db, this.usersCollection, userId, this.downloadsSubcollection);
    const q = query(downloadsRef, orderBy('downloadedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const downloads: UserDownload[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      downloads.push({
        id: doc.id,
        wallpaperId: data.wallpaperId,
        resolution: data.resolution,
        downloadedAt: data.downloadedAt?.toDate() || new Date(),
        deviceType: data.deviceType,
        isFromPack: data.isFromPack
      });
    });
    
    return downloads;
  }
}

export const userService = new UserService();